"use client";

import React from "react";
import { MapPin, Phone, Mail, Loader2 } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
const contactInfo = [{
  icon: MapPin,
  title: "ADDRESS",
  details: "124 Sherwood drive Brantford"
}, {
  icon: Phone,
  title: "Project Hotline",
  details: "+1 (289) 439-5348"
}, {
  icon: Mail,
  title: "Direct Inquiry",
  details: "sampson.info101@gmail.com"
}];
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="71131" className="flex flex-col items-center justify-center p-8 border border-border bg-card/50 backdrop-blur-xl text-center h-full min-h-[400px] rounded-none">
        <h3 className="text-2xl font-default font-bold text-foreground mb-4">Inquiry Received</h3>
        <p className="text-foreground/90 mb-8 font-sans">{successMessage}</p>
        <Button onClick={resetForm} className="rounded-none border border-primary bg-primary text-primary-foreground hover:opacity-90 transition-all font-default uppercase tracking-wider">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 backdrop-blur-xl bg-card/30 p-8 border border-border/50 rounded-none">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Alex" className="rounded-none border-border bg-muted/30 text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="Rivers" className="rounded-none border-border bg-muted/30 text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Please enter a valid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Email Address</FormLabel>
            <FormControl>
              <Input type="email" placeholder="a.rivers@industrial-core.com" className="rounded-none border-border bg-muted/30 text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required",
      minLength: {
        value: 10,
        message: "Message must be at least 10 characters long"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-mono uppercase text-xs tracking-widest">Project Scope</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe your industrial requirements or technical specifications..." className="min-h-[150px] rounded-none border-border bg-muted/30 text-foreground focus-visible:ring-2 focus-visible:ring-primary focus-visible:border-primary resize-y" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 border border-destructive bg-destructive/10 text-destructive text-sm font-mono">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto px-10 py-7 rounded-none border-none bg-primary text-primary-foreground hover:opacity-90 hover:scale-[1.01] active:scale-[0.98] transition-all text-base font-default font-bold uppercase tracking-widest">
        {isSubmitting ? <>
            <Loader2 className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </> : "Send Inquiry"}
      </Button>
    </Form>;
}
export default function ContactDetails() {
  return <section id="contact-us" className="bg-background text-foreground py-16 md:py-24">
      <div className="container mx-auto max-w-[1200px] px-4 md:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-24">
          
          {/* Contact Information Column */}
          <div className="md:col-span-5 flex flex-col justify-center">
            <div className="mb-12">
              <h2 className="text-4xl md:text-5xl font-default font-bold text-foreground mb-6 tracking-tight">
                Start the Conversation
              </h2>
              <p className="text-lg leading-relaxed !text-[hsl(0,0%,0%)] !font-bold">We offer great quality and competitive prices to all of our customers. With our dedication to quality and professional friendly customer service we are ready to answer all your questions!</p>
            </div>

            <div className="space-y-10">
              {contactInfo.map((item, i) => {
              const Icon = item.icon;
              return <div key={i} data-index={i} className="flex items-start gap-6">
                    <div className="mt-1 shrink-0 bg-muted p-3 rounded-none">
                      <Icon className="w-6 h-6 text-primary" strokeWidth={2} />
                    </div>
                    <div>
                      <h4 className="text-xl font-default font-bold text-foreground mb-2">
                        {item.title}
                      </h4>
                      <p className="text-muted-foreground text-base font-sans">
                        {item.details}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>

          {/* Form Column */}
          <div className="md:col-span-7">
            <FormProvider formKey="contact_form_home" formId="contact_form_home" sectionName="Contact Details" defaultValues={{
            firstName: "",
            lastName: "",
            email: "",
            message: ""
          }} submitText="Send Inquiry" successMessage="Our industrial team has received your inquiry and will respond within 24 business hours.">
              <ContactFormInner />
            </FormProvider>
          </div>

        </div>
      </div>
    </section>;
}